import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";

// Components
import PackageStatus from "../PackageStatus";
import SerialNumber from "../SerialNumber";
import PackageDetails from "../PackageDetails";
import PackageImages from "./PackageImages";
import PackageItems from "./PackageItems";
import EditPackageDrawer from "../EditPackageDrawer";
import ReceivingSlip from "./ReceivingSlip";

// Icons
import edit from "../../assets/svg/edit.svg";
import slip from "../../assets/svg/slip.svg";

// Custom Hook
import { useShippingRequestTracking } from "./hooks/useShippingRequestTracking";
import { useDispatch, useSelector } from "react-redux";
import AccordionViaTableRowName from "../AccordionViaTableRowName";
import { setActiveItem } from "../../store/commonSlice";
import AddItemModal from "./AddItemModel";
import axios from "axios";
import { getFunctions, httpsCallable } from "firebase/functions";

const ShippingRequestTracking = ({ steps, activeStep }) => {
  const dispatch = useDispatch();
  // Custom hook for business logic
  const {
    activeComponent,
    selectedItem,
    packageActiveData,
    userActiveData,
    packageItems,
    barcodeValue,
    dateForSlip,
    handlePrepareShipment,
    handleSlipDownload,
  } = useShippingRequestTracking();

  // Local UI state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editPackageData, setEditPackageData] = useState({});
  const [addItemModelOpen, setAddItemModelOpen] = useState(false);

  // Debug effect to log package data changes
  useEffect(() => {
    console.log("PackageActiveData updated:", packageActiveData);
    console.log("PackageImages:", packageActiveData?.packageImages);
    console.log("PackagePhotos:", packageActiveData?.packagePhotos);
  }, [packageActiveData]);

  // Force re-render when package data changes
  const [forceUpdate, setForceUpdate] = useState(0);
  
  useEffect(() => {
    if (packageActiveData) {
      setForceUpdate(prev => prev + 1);
    }
  }, [packageActiveData?.packageImages, packageActiveData?.packagePhotos]);

  // Toggle edit drawer
  const toggleDrawer = (packageData) => {
    setEditPackageData(packageData);
    setDrawerOpen(!drawerOpen);
    
    // Force re-render when drawer is closed to ensure fresh data
    if (drawerOpen) {
      setForceUpdate(prev => prev + 1);
    }
  };

  if (!packageActiveData) {
    return (
      <Box sx={{ padding: "20px", textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Select a package to view details
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {activeComponent === "Actions" && !selectedItem ? <Box sx={{ paddingTop: "16px", paddingRight: "20px" }}>
        {/* Header */}
        <Box sx={{ marginLeft: "20px", mb: "16px" }}>
          <Typography variant="h5">
            Tracking: {packageActiveData?.trackingNumber || "N/A"}
          </Typography>
        </Box>

        {/* Package Status */}
        <PackageStatus steps={steps} activeStep={activeStep} />

        {/* Serial Number and Package Details */}
        <SerialNumber
          SerialNumber={packageActiveData?.serialNumber || "-"}
          packageTotalAmount={packageActiveData?.packageTotalAmount || packageActiveData?.totalAmount}
          shipmentData={userActiveData}
          pageName="shippingRequest"
        />

        <PackageDetails packageActiveData={packageActiveData} />

        {/* Action Buttons */}
        <Box
          sx={{
            marginTop: "40px",
            display: "flex",
            justifyContent: "center",
            borderBottom: "1px solid #E5E5E8",
            paddingBottom: "30px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {/* Edit Package Button */}
          <Button
            variant="contained"
            sx={{
              backgroundColor: "white",
              color: "#481AA3",
              border: "1px solid #E5E5E8",
              borderRadius: "4px",
              boxShadow: "none",
              whiteSpace: "nowrap",
              marginTop: "20px",
              "&:hover": {
                color: "#481AA3",
                backgroundColor: "white",
              },
            }}
            onClick={() => toggleDrawer(packageActiveData)}
          >
            <img src={edit} alt="edit" style={{ marginRight: "10px" }} />
            Edit Package Details
          </Button>

          {/* Receiving Slip Button */}
          <Button
            variant="contained"
            sx={{
              backgroundColor: "white",
              color: "#481AA3",
              border: "1px solid #E5E5E8",
              boxShadow: "none",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              marginTop: "20px",
              "&:hover": {
                color: "#481AA3",
                backgroundColor: "white",
              },
            }}
            disabled={!packageActiveData?.serialNumber}
            onClick={handleSlipDownload}
          >
            <img src={slip} alt="slip" style={{ marginRight: "10px" }} />
            Receiving Slip
          </Button>

          {/* Prepare Shipment Button */}
          <Button
            variant="contained"
            onClick={() =>
              packageActiveData?.packagePhotos?.length > 0
                ? handlePrepareShipment(packageActiveData?.id)
                : toggleDrawer(packageActiveData)
            }
            disabled={
              packageActiveData?.packageStatus !== "PREPARE_SHIPMENT_REQUESTED" &&
              packageActiveData?.packageStatus !== "RE_PREPARE_PACKAGE"
            }
            sx={{
              backgroundColor: "white",
              color: "#481AA3",
              border: "1px solid #E5E5E8",
              boxShadow: "none",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              marginTop: "20px",
              "&:hover": {
                color: "#481AA3",
                backgroundColor: "white",
              },
            }}
          >
            {packageActiveData?.packageStatus === "PREPARE_SHIPMENT_REQUESTED"
              ? "Prepare Shipment"
              : "Reprepare Package"
            }
          </Button>
        </Box>

        {/* Edit Package Drawer */}
        <EditPackageDrawer
          pageName="shippingRequest"
          open={drawerOpen}
          onClose={toggleDrawer}
          collectionName="shelfPackages"
          shipmentCollectionName="prepareShipmentRequests"
          shipmentData={userActiveData}
        />

        {/* Hidden Receiving Slip for PDF generation */}
        <Box sx={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReceivingSlip
            id="receiving-slip"
            name={packageActiveData?.suiteNumber ? packageActiveData?.name : "No Name"}
            date={dateForSlip}
            suiteNumber={packageActiveData?.suiteNumber ? packageActiveData?.suiteNumber : "No Suite"}
            packageId={packageActiveData?.packageId}
            barcodeValue={packageActiveData?.serialNumber ? packageActiveData?.serialNumber : barcodeValue}
          />
        </Box>

        {/* Package Images */}
        <PackageImages
          key={`${packageActiveData?.id}-${forceUpdate}-${JSON.stringify(packageActiveData?.packageImages || packageActiveData?.packagePhotos)}`}
          images={packageActiveData?.packageImages || packageActiveData?.packagePhotos}
          title="Package Images"
        />

        {/* Package Items */}
        <PackageItems
          items={packageItems}
          packageActiveData={packageActiveData}
          userActiveData={userActiveData}
          collection="shelfPackages"
          shipmentCollection="prepareShipmentRequests"
          addItemButton={
            <Button
              color="primary"
              onClick={() => {
                setAddItemModelOpen(true);
              }}
            >
              Add Item
            </Button>
          }
        />
      </Box> : selectedItem ? <AccordionViaTableRowName
        handleBack={()=>{dispatch(setActiveItem(null))}}
        packageClickHandler={()=>{dispatch(setActiveItem(null))}}
        id={selectedItem}
        packageActiveData={packageActiveData}
        userActiveData={userActiveData}
        packageItems={packageItems}
        pageName="shippingRequest"
        packageCollection={"shelfPackages"}
        shipmentCollection={"prepareShipmentRequests"}
      /> : null}
       <AddItemModal
          open={addItemModelOpen}
          toggleModel={() => setAddItemModelOpen(!addItemModelOpen)}
        />
    </>
  );
};

export default ShippingRequestTracking; 